import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ParentLinkForm } from '@/features/parent/components/ParentLinkForm'
import { ChildDashboardPanel } from '@/features/parental/components/ChildDashboardPanel'
import { RestrictionsForm } from '@/features/parental/components/RestrictionsForm'
import { useLinkedChildren } from '@/features/parental/hooks/useParental'

export function ParentDashboardPage() {
  const { t } = useTranslation()
  const { data: children, isLoading } = useLinkedChildren()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const linked = (children ?? []).filter((entry) => entry.child !== null)
  // Sans choix explicite, le premier enfant est affiché : un parent qui n'en a
  // qu'un ne devrait pas avoir à cliquer pour voir quoi que ce soit.
  const currentId = selectedId ?? linked[0]?.child?.id ?? null

  return (
    <div className="min-h-full overflow-y-auto bg-surface px-container-px-mobile pb-20 pt-6 md:px-container-px-desktop md:pb-8">
      <div className="mx-auto flex max-w-3xl flex-col gap-8">
        <h1 className="font-display text-2xl font-bold text-on-surface">
          {t('parent.dashboard_title')}
        </h1>

        <section className="rounded-2xl border border-surface-200 bg-white p-6">
          <ParentLinkForm />
        </section>

        {isLoading && <div className="h-40 animate-pulse rounded-2xl bg-surface-container" />}

        {!isLoading && linked.length === 0 && (
          <section className="rounded-3xl bg-surface-low p-8 text-center">
            <p className="font-medium text-on-surface">{t('parent.no_children')}</p>
            <p className="mt-1 text-sm text-on-surface-variant">{t('parent.no_children_hint')}</p>
          </section>
        )}

        {linked.length > 1 && (
          <div role="tablist" aria-label={t('parent.children')} className="flex flex-wrap gap-2">
            {linked.map((entry) => (
              <button
                key={entry.child?.id}
                type="button"
                role="tab"
                aria-selected={currentId === entry.child?.id}
                onClick={() => setSelectedId(entry.child?.id ?? null)}
                className={[
                  'min-h-11 rounded-full px-4 text-sm font-semibold transition-colors',
                  currentId === entry.child?.id
                    ? 'bg-primary text-white'
                    : 'bg-surface-low text-on-surface-variant hover:bg-surface-container',
                ].join(' ')}
              >
                {entry.child?.first_name ?? entry.child?.email}
              </button>
            ))}
          </div>
        )}

        {currentId !== null && (
          <>
            <ChildDashboardPanel childId={currentId} />

            <section className="rounded-2xl border border-surface-200 bg-white p-6">
              <RestrictionsForm childId={currentId} />
            </section>
          </>
        )}
      </div>
    </div>
  )
}
